/**
 * Poses como dados. Cada osso recebe uma rotação em graus [x, y, z], nos eixos do personagem
 * (x = esquerda do atleta, y = para cima, z = para a frente), relativa à pose de repouso em T.
 *
 * Semântica (aplicada por `applyPose` em packages/athlete-three): a rotação de um osso é
 * descrita como se o pai estivesse em repouso, e depois carregada pela rotação acumulada do pai.
 * Assim, "dobrar o cotovelo 90°" significa a mesma coisa com o braço em qualquer posição.
 */
export type Euler = readonly [number, number, number];

export const ANIM_BONES = [
  "pelvis",
  "spine_01",
  "spine_02",
  "spine_03",
  "neck_01",
  "Head",
  "clavicle_l",
  "upperarm_l",
  "lowerarm_l",
  "hand_l",
  "clavicle_r",
  "upperarm_r",
  "lowerarm_r",
  "hand_r",
  "thigh_l",
  "calf_l",
  "foot_l",
  "thigh_r",
  "calf_r",
  "foot_r",
] as const;
export type AnimBone = (typeof ANIM_BONES)[number];
export type Pose = Partial<Record<AnimBone, Euler>>;

const ZERO: Euler = [0, 0, 0];

/** Soma de poses (camadas aditivas), com peso na segunda. */
export function addPoses(base: Pose, layer: Pose, weight = 1): Pose {
  const out: Record<string, Euler> = { ...base };
  for (const [bone, e] of Object.entries(layer) as [AnimBone, Euler][]) {
    const b = out[bone] ?? ZERO;
    out[bone] = [b[0] + e[0] * weight, b[1] + e[1] * weight, b[2] + e[2] * weight];
  }
  return out as Pose;
}

/** Interpolação linear entre duas poses. */
export function lerpPoses(a: Pose, b: Pose, t: number): Pose {
  const out: Record<string, Euler> = {};
  const bones = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<AnimBone>;
  for (const bone of bones) {
    const x = a[bone] ?? ZERO;
    const y = b[bone] ?? ZERO;
    out[bone] = [x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t];
  }
  return out as Pose;
}

/** Espelha uma pose (lado esquerdo ↔ direito), útil para gestos com a outra mão. */
export function mirrorPose(pose: Pose): Pose {
  const out: Record<string, Euler> = {};
  for (const [bone, e] of Object.entries(pose) as [AnimBone, Euler][]) {
    const other = bone.endsWith("_l")
      ? bone.slice(0, -2) + "_r"
      : bone.endsWith("_r")
        ? bone.slice(0, -2) + "_l"
        : bone;
    out[other] = [e[0], -e[1], -e[2]];
  }
  return out as Pose;
}
